import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from '../lib/translations';
import { ChevronRight, ChevronLeft, X, Sparkles, Zap, ArrowRight, Monitor, Smartphone } from 'lucide-react';

interface TourStep {
  targetId: string;
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface ProductTourProps {
  onComplete: () => void;
  onSkip: () => void;
  role: 'teacher' | 'student';
}

const ProductTour: React.FC<ProductTourProps> = ({ onComplete, onSkip, role }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const tourRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const steps: TourStep[] = [
    {
      targetId: 'tour-welcome',
      title: t.tour.steps.welcome.title,
      content: t.tour.steps.welcome.content,
      position: 'center'
    },
    {
      targetId: 'tour-schedule',
      title: t.tour.steps.schedule.title,
      content: t.tour.steps.schedule.content,
      position: 'bottom'
    },
    {
      targetId: 'tour-learners',
      title: t.tour.steps.learners.title,
      content: t.tour.steps.learners.content,
      position: 'left'
    },
    {
        targetId: 'nav-students',
        title: t.tour.steps.students.title,
        content: t.tour.steps.students.content,
        position: 'right'
    },
    {
      targetId: 'nav-planner',
      title: t.tour.steps.planner.title,
      content: t.tour.steps.planner.content,
      position: 'right'
    },
    {
        targetId: 'nav-curriculum',
        title: t.tour.steps.curriculum.title,
        content: t.tour.steps.curriculum.content,
        position: 'right'
    },
    {
        targetId: 'nav-insights',
        title: t.tour.steps.insights.title,
        content: t.tour.steps.insights.content,
        position: 'right'
    },
    {
        targetId: 'nav-reviews',
        title: t.tour.steps.reviews.title,
        content: t.tour.steps.reviews.content,
        position: 'right'
    },
    {
      targetId: 'nav-public-presence',
      title: t.tour.steps.publicPresence.title,
      content: t.tour.steps.publicPresence.content,
      position: 'right'
    },
    {
      targetId: 'nav-profile',
      title: t.tour.steps.settings.title,
      content: t.tour.steps.settings.content,
      position: 'right'
    }
  ];

  const activeSteps = role === 'teacher' 
    ? steps 
    : steps.filter(s => !['nav-planner', 'nav-students', 'nav-curriculum', 'nav-insights', 'nav-reviews', 'nav-public-presence'].includes(s.targetId));

  const updatePosition = () => {
    const step = activeSteps[currentStep];
    
    // On mobile, everything behaves like a centered modal
    if (isMobile || step.position === 'center') {
      setCoords({ top: 0, left: 0, width: 0, height: 0 });
      setIsVisible(true);
      return;
    }

    const element = document.getElementById(step.targetId);
    if (element) {
      const rect = element.getBoundingClientRect();
      const newCoords = {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      };
      setCoords(newCoords);
      
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsVisible(true);
    } else {
      if (currentStep < activeSteps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        onComplete();
      }
    }
  };

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      updatePosition();
    };

    const timer = setTimeout(updatePosition, 100);
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [currentStep, activeSteps, isMobile]);

  useLayoutEffect(() => {
    if (!isVisible || !tourRef.current) return;

    const step = activeSteps[currentStep];
    const tooltipRect = tourRef.current.getBoundingClientRect();
    const padding = 20;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let finalTop = 0;
    let finalLeft = 0;

    if (isMobile || step.position === 'center') {
      finalTop = (windowHeight / 2) - (tooltipRect.height / 2);
      finalLeft = (windowWidth / 2) - (tooltipRect.width / 2);
    } else {
      if (step.position === 'bottom') {
        finalTop = coords.top + coords.height + 15;
        finalLeft = coords.left + (coords.width / 2) - (tooltipRect.width / 2);
      } else if (step.position === 'top') {
        finalTop = coords.top - tooltipRect.height - 15;
        finalLeft = coords.left + (coords.width / 2) - (tooltipRect.width / 2);
      } else if (step.position === 'left') {
        finalTop = coords.top + (coords.height / 2) - (tooltipRect.height / 2);
        finalLeft = coords.left - tooltipRect.width - 15;
      } else if (step.position === 'right') {
        finalTop = coords.top + (coords.height / 2) - (tooltipRect.height / 2);
        finalLeft = coords.left + coords.width + 15;
      }

      finalLeft = Math.max(padding, Math.min(finalLeft, windowWidth - tooltipRect.width - padding));
      finalTop = Math.max(padding + window.scrollY, Math.min(finalTop, windowHeight + window.scrollY - tooltipRect.height - padding));
    }

    setTooltipPos({ top: finalTop, left: finalLeft });
  }, [isVisible, coords, currentStep, isMobile]);

  const handleNext = () => {
    if (currentStep < activeSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = activeSteps[currentStep];

  return (
    <div className="fixed inset-0 z-[10000] overflow-hidden pointer-events-auto">
      {/* Dimmed Overlay */}
      <div 
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-[2px]"
        style={{
          clipPath: (isMobile || step.position === 'center') 
            ? 'none' 
            : `polygon(
                0% 0%, 
                0% 100%, 
                ${coords.left - 8}px 100%, 
                ${coords.left - 8}px ${coords.top - 8}px, 
                ${coords.left + coords.width + 8}px ${coords.top - 8}px, 
                ${coords.left + coords.width + 8}px ${coords.top + coords.height + 8}px, 
                ${coords.left - 8}px ${coords.top + coords.height + 8}px, 
                ${coords.left - 8}px 100%, 
                100% 100%, 
                100% 0%
              )`
        }}
      />

      {/* Floating Tooltip / Modal */}
      <div 
        ref={tourRef}
        className={`
          absolute glass-card w-[calc(100%-2rem)] sm:w-[360px] p-8 shadow-2xl border-white/20 dark:border-white/5 animate-in zoom-in duration-200 pointer-events-auto
          ${(isMobile || step.position === 'center') ? 'fixed' : ''}
        `}
        style={{
          top: `${tooltipPos.top}px`,
          left: `${tooltipPos.left}px`,
          visibility: isVisible ? 'visible' : 'hidden'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-1">
            {activeSteps.map((_, i) => (
              <div 
                key={i} 
                className={`h-1 rounded-full transition-all duration-300 ${i === currentStep ? 'w-6 bg-purple-600' : 'w-2 bg-slate-200 dark:bg-slate-700'}`} 
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {isMobile ? <Smartphone size={12} className="text-slate-400" /> : <Monitor size={12} className="text-slate-400" />}
            <button onClick={onSkip} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
             <div className="p-2 bg-purple-600 rounded-lg text-white shrink-0">
                <span className="animate-ping absolute inline-flex h-8 w-8 rounded-lg bg-purple-400 opacity-20"></span>
                <Sparkles size={16} className="relative" />
             </div>
             <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">{step.title}</h3>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
            {step.content}
          </p>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <button 
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${currentStep === 0 ? 'opacity-0 pointer-events-none' : 'text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
          >
            <ChevronLeft size={14} /> {t.tour.prev}
          </button>

          <button 
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg font-black text-[10px] uppercase tracking-widest shadow-xl shadow-purple-500/20 hover:bg-purple-700 transition-all active:scale-95"
          >
            {currentStep === activeSteps.length - 1 ? t.tour.finish : t.tour.next}
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="absolute bottom-8 right-8">
        <button 
          onClick={onSkip}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white/60 hover:text-white rounded-lg font-black text-[9px] uppercase tracking-widest transition-all"
        >
          {t.tour.skip} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};

export default ProductTour;